"use client";

import { ArrowRight, CheckCircle2, ClipboardList, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { twc } from "../utils";

const CardButton = twc.button`group w-full text-left rounded-2xl bg-primary-p900 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-white shadow-lg cursor-pointer transition-colors hover:bg-primary-p900/90 disabled:cursor-wait disabled:hover:bg-primary-p900`;
const Label = twc.span`text-xs font-semibold uppercase tracking-wide text-white/60`;
const Title = twc.span`block text-lg font-semibold leading-snug line-clamp-2`;
const Description = twc.span`block text-sm leading-relaxed text-white/70 line-clamp-2`;

export type StageTransitionVariant = "group" | "milestone";

export type StageTransitionCardProps = {
  title: string;
  description?: string;
  onContinue: () => void;
  icon?: ReactNode;
  /**
   * "group" (default) — routine group transition, label "Next up".
   * "milestone" — high-level milestone reached, label "Milestone reached"
   * with a check-circle icon. Driven by the milestone payload in the
   * `save_intake_fields` transition output.
   */
  variant?: StageTransitionVariant;
  /**
   * When true, the trailing arrow is replaced with a spinner and the card
   * is disabled. Used while waiting for the assistant turn to finish
   * streaming after the user has already clicked.
   */
  loading?: boolean;
};

export function StageTransitionCard({
  title,
  description,
  onContinue,
  icon,
  variant = "group",
  loading = false,
}: StageTransitionCardProps) {
  const isMilestone = variant === "milestone";
  const labelText = isMilestone ? "Milestone reached" : "Next up";
  const ariaContinue = isMilestone ? `Continue past ${title}` : `Continue to ${title}`;
  const ariaContinuing = isMilestone ? `Continuing past ${title}` : `Continuing to ${title}`;
  const defaultIcon = isMilestone ? (
    <CheckCircle2 className="h-5 w-5 text-white/90" aria-hidden="true" />
  ) : (
    <ClipboardList className="h-5 w-5 text-white/80" aria-hidden="true" />
  );

  return (
    <CardButton
      type="button"
      onClick={onContinue}
      disabled={loading}
      aria-label={loading ? ariaContinuing : ariaContinue}
      aria-busy={loading || undefined}
      aria-live="polite"
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
          {icon ?? defaultIcon}
        </div>

        <div className="min-w-0 flex-1">
          <Label>{labelText}</Label>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
        </div>

        <div
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 transition-colors group-hover:bg-white/25 group-disabled:group-hover:bg-white/15"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          ) : (
            <ArrowRight className="h-5 w-5 text-white" />
          )}
        </div>
      </div>
    </CardButton>
  );
}
